import React from 'react';
import {LOCATION, LIZ_ACCOUNTING} from "../../../global/Server/endpoints";
import {apiRequest} from "../../../../redux/actions/api";
import FeedbackBar from "../../../feedbackBar/FeedbackBar";
import {userInfoSelector} from "../../../../redux/selectors/auth";
import withShipment from "../../../../withShipment";
import "./ATMReceipt.css"
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent, DialogContentText,
  DialogTitle,
  FormGroup,
  Grid,
  TextField
} from "@material-ui/core";
import CustomToolbar from "../../../global/CustomToolbar";
import { addDays, endOfMonth, format, startOfMonth, subDays } from 'date-fns';
import FolderCard from "../liz/FolderCard";
import {MONTHS} from "./constant";
import KeyboardBackspaceIcon from "@material-ui/icons/KeyboardBackspace";
import EditIcon from '@mui/icons-material/Edit';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InputAdornment from "@material-ui/core/InputAdornment";
import {Button, FormControlLabel} from "@mui/material";
import RefreshIcon from "@material-ui/icons/Refresh";
import { v4 as uuidv4 } from 'uuid';
import CloseIcon from "@mui/icons-material/Close";


class ATMReceipt extends React.Component {
  constructor(props) {
    super(props);

    const startYear = 2025;
    const currentYear = new Date().getFullYear();

    const YEARS = [];
    for (let year = startYear; year <= currentYear; year++) {
      YEARS.push(year);
    }

    this.state = {
      YEARS,
      locations: [],
      profitSummary: [],
      feedbackBarOpen: false,
      feedbackStatus: '',
      feedbackMessage: '',
      selectedYear: null,
      selectedMonth: null,
      selectedWeek: null,
      atmSummary: null,
      autoRows: [],
      lastWeekRows: [],
      manualNextWeekRows: [],
      originalManualRows: [],
      saving: false,
      showATM: true,
      showLastWeek: true,
      showNextWeek: true,
      showDidNotUse: true,

      confirmRefreshOpen: false,
      refreshing: false,
    };
  }

  async componentDidMount() {
    await this.fetchLocations();

    const result = this.getLastCompletedAccountingWeek();

    if (!result) return;

    const { year, monthIndex, week } = result;

    this.setState(
      {
        selectedYear: year,
        selectedMonth: monthIndex,
      },
      async () => {
        await this.openWeek(week);
      }
    );
  }

  openRefreshConfirm = () => {
    this.setState({ confirmRefreshOpen: true });
  };

  fetchPrevWeekTotals = () => {
    const { selectedYear, selectedMonth, selectedWeek } = this.state;
    if (!selectedWeek) return Promise.resolve({});

    const prevWeekInfo = this.getPreviousWeek(
      selectedYear,
      selectedMonth,
      selectedWeek.weekNumber
    );
    if (!prevWeekInfo) return Promise.resolve({});

    return new Promise((resolve) => {
      this.props.apiRequest({
        key: 'atm/prevWeekData',
        endpoint: LIZ_ACCOUNTING.GET_ATM_WEEK_DATA,
        data: {
          year: prevWeekInfo.year,
          month: prevWeekInfo.monthIndex + 1,
          weekNumber: prevWeekInfo.week.weekNumber,
        },
        silent: true,
        onError: () => resolve({}),
        onSuccess: (payload) => {
          if (!payload?.success || !payload.week) {
            resolve({});
            return;
          }

          const prevAutoRows = payload.week.autoRows || [];
          const prevManualRows = payload.week.manualNextWeekRows || [];
          const allNextWeekRows = [...prevAutoRows, ...prevManualRows];

          const totalsMap = {};

          allNextWeekRows.forEach((r) => {
            const manager = r.manager?.trim();
            if (!manager || manager === 'undefined' || manager === 'null') return;
            if (!totalsMap[manager]) totalsMap[manager] = 0;
            totalsMap[manager] +=
              (r.eric || 0) + (r.profit || 0) + (r.atmDidntUse || 0);
          });

          resolve(totalsMap);
        },
      });
    });
  };

  refreshFromManagerSummary = async () => {
    const { selectedWeek, locations, autoRows } = this.state;

    if (!selectedWeek) {
      this.openFeedback('error', 'Please select a week before refreshing');
      return;
    }

    if (!locations.length) {
      this.openFeedback('error', 'No locations available to fetch summary');
      return;
    }

    this.setState({ refreshing: true });

    const prevTotalsMap = await this.fetchPrevWeekTotals();

    const from = format(selectedWeek.fromDate, 'yyyy-MM-dd');
    const to = format(selectedWeek.toDate, 'yyyy-MM-dd');

    this.props.apiRequest({
      key: 'atm/refreshLocationSummary',
      endpoint: LIZ_ACCOUNTING.LOCATION_SUMMARY,
      data: {
        fromDate: from,
        toDate: to,
        locations: locations.map((l) => l.name),
      },
      silent: true,
      onError: (err) => {
        this.openFeedback('error', `Failed to fetch ATM summary: ${err}`);
        this.setState({ refreshing: false, confirmRefreshOpen: false });
      },
      onSuccess: (res) => {
        if (!res?.success) {
          this.openFeedback('error', 'Failed to fetch ATM summary');
          this.setState({ refreshing: false, confirmRefreshOpen: false });
          return;
        }

        const atmSummaries = res.sessions
          .map((session) => {
            const loc = locations.find(
              (l) =>
                l.name.toUpperCase() === session.locationName?.toUpperCase(),
            );
            if (!loc) return null;
            return { location: loc.name, summary: session.managerSummary };
          })
          .filter(Boolean);

        const newAutoRows = Object.entries(
          atmSummaries.reduce((acc, { summary }) => {
            const manager = summary.managerName?.trim();
            if (!manager) return acc;

            if (!acc[manager])
              acc[manager] = {
                id: uuidv4(),
                manager,
                eric: 0,
                profit: 0,
                atmDidntUse: 0,
                advEly: 0,
                isEditing: false,
              };

            acc[manager].eric += this.getAmount(
              summary.atmTransactions,
              'ATM_CASH'
            );
            acc[manager].profit += this.getAmount(
              summary.atmTransactions,
              'ATM_ADV'
            );
            acc[manager].atmDidntUse += this.getAmount(
              summary.atmTransactions,
              'ATM_DIDNT_USE'
            );

            return acc;
          }, {})
        ).map(([_, row]) => row);

        const mergedAutoRows = autoRows.map((r) => ({ ...r }));

        newAutoRows.forEach((newRow) => {
          const index = mergedAutoRows.findIndex(
            (r) => r.manager === newRow.manager && !r.isEditing
          );

          if (index !== -1) {
            mergedAutoRows[index] = {
              ...mergedAutoRows[index],
              eric: newRow.eric,
              profit: newRow.profit,
              atmDidntUse: newRow.atmDidntUse,
              advEly: newRow.advEly,
            };
          } else {
            mergedAutoRows.push({ ...newRow });
          }
        });

        this.setState({
          atmSummary: atmSummaries,
          autoRows: mergedAutoRows,
          _prevTotalsMap: prevTotalsMap,
          refreshing: false,
          confirmRefreshOpen: false,
        });

        this.openFeedback('success', 'ATM values refreshed successfully');
      },
    });
  };

  getLastCompletedAccountingWeek = () => {
    const today = new Date();

    let year = today.getFullYear();
    let monthIndex = today.getMonth();

    for (let i = 0; i < 12; i++) {
      const weeks = this.generateWeeksForMonth(year, monthIndex);

      const lastWeek = [...weeks].reverse().find((w) => w.toDate < today);

      if (lastWeek) {
        return { year, monthIndex, week: lastWeek };
      }

      monthIndex--;
      if (monthIndex < 0) {
        monthIndex = 11;
        year--;
      }
    }

    return null;
  };

  initAutoRows = () => {
    const autoRows = this.getNextWeekAutoRows().map((r) => ({
      ...r,
      isEditing: false,
    }));
    this.setState({ autoRows });
  };

  toggleSection = (key) => {
    this.setState((prev) => ({ [key]: !prev[key] }));
  };

  sumBy = (arr, fn) => arr.reduce((t, x) => t + fn(x), 0);

  getAmount = (txs, type) =>
    this.sumBy(txs?.filter((t) => t.type === type) || [], (t) => t.amount || 0);

  groupByManager = () => {
    const map = {};
    (this.state.atmSummary || []).forEach(({ summary }) => {
      const name = summary.managerName?.trim();
      if (!name || name === 'undefined' || name === 'null') return;
      if (!map[name]) map[name] = [];
      map[name].push(summary);
    });
    return map;
  };

  getATMOverviewRows = () => {
    return (this.state.atmSummary || [])
      .map(({ location, summary }) => {
        const fromEric = this.getAmount(summary.atmTransactions, 'ATM_CASH');
        const fromProfit = this.getAmount(
          summary.atmTransactions,
          'ATM_FROM_PROFIT'
        );

        return {
          location,
          fromEric,
          fromProfit,
          total: fromEric + fromProfit,
        };
      })
      .filter((r) => r.total > 0);
  };

  getDidNotUseRows = () => {
    return (this.state.atmSummary || [])
      .map(({ location, summary }) => ({
        id: `${summary.managerName}-${location}`,
        location,
        manager: summary.managerName,
        amount: this.getAmount(summary.atmTransactions, 'ATM_DIDNT_USE'),
      }))
      .filter(
        (row) =>
          row.amount > 0 &&
          row.manager &&
          row.manager !== 'undefined' &&
          row.manager !== 'null'
      );
  };

  initLastWeekRows = (prevTotals = {}) => {

    const existing = this.state.lastWeekRows || [];

    const rows = Object.entries(prevTotals).map(([manager, atmLastWeek]) => {

      const existingRow = existing.find(r => r.manager === manager);
      const midWeekAdv = existingRow ? existingRow.midWeekAdv : 0;

      return {
        manager,
        atmLastWeek: Number(atmLastWeek || 0),
        midWeekAdv,
        total: Number(atmLastWeek || 0) + midWeekAdv,
        isEditing: false
      };
    });

    this.setState({ lastWeekRows: rows });
  };
  getLastWeekRows = () => {
    return this.state.lastWeekRows;
  };

  toggleLastWeekRowEdit = (manager) => {
    this.setState((prev) => ({
      lastWeekRows: prev.lastWeekRows.map((r) =>
        r.manager === manager
          ? { ...r, isEditing: !r.isEditing, _original: r.isEditing ? undefined : { ...r } }
          : r
      ),
    }));
  };

  cancelLastWeekRowEdit = (manager) => {
    this.setState((prev) => ({
      lastWeekRows: prev.lastWeekRows.map((r) =>
        r.manager === manager && r._original
          ? { ...r._original, isEditing: false, _original: undefined }
          : r
      ),
    }));
  };

  updateLastWeekRow = (manager, value) => {
    const midWeekAdv = value === '' ? 0 : Number(value);
    this.setState((prev) => ({
      lastWeekRows: prev.lastWeekRows.map((r) =>
        r.manager === manager
          ? { ...r, midWeekAdv, total: r.atmLastWeek + midWeekAdv }
          : r
      ),
    }));
  };

  getNextWeekAutoRows = () => {
    const grouped = this.groupByManager();

    return Object.entries(grouped)
      .filter(
        ([manager]) => manager && manager !== 'undefined' && manager !== 'null'
      )
      .map(([manager, summaries]) => {
        const eric = this.sumBy(summaries, (s) =>
          this.getAmount(s.atmTransactions, 'ATM_CASH')
        );

        const profit = this.sumBy(summaries, (s) =>
          this.getAmount(s.atmTransactions, 'ATM_ADV')
        );

        const atmDidntUse = this.sumBy(summaries, (s) =>
          this.getAmount(s.atmTransactions, 'ATM_DIDNT_USE')
        );

        return {
          id: uuidv4(),
          manager,
          eric,
          profit,
          atmDidntUse,
          advEly: 0,
          total: eric + profit + atmDidntUse,
          isEditing: false,
        };
      });
  };

  toggleAutoRowEdit = (id) => {
    this.setState((prev) => ({
      autoRows: prev.autoRows.map((r) =>
        r.id === id
          ? { ...r, isEditing: !r.isEditing, _original: r.isEditing ? undefined : { ...r } }
          : r
      ),
    }));
  };

  cancelAutoRowEdit = (id) => {
    this.setState((prev) => ({
      autoRows: prev.autoRows.map((r) =>
        r.id === id && r._original
          ? { ...r._original, isEditing: false, _original: undefined }
          : r
      ),
    }));
  };

  updateAutoRow = (id, field, value) => {
    this.setState({
      autoRows: this.state.autoRows.map((r) =>
        r.id === id
          ? {
              ...r,
              [field]:
                field === 'manager' ? value : value === '' ? '' : Number(value),
            }
          : r
      ),
    });
  };

  addManualRow = () => {
    this.setState({
      manualNextWeekRows: [
        ...this.state.manualNextWeekRows,
        {
          id: uuidv4(),
          manager: '',
          eric: 0,
          profit: 0,
          atmDidntUse: 0,
          advEly: 0,
          isEditing: true,
        },
      ],
    });
  };

  updateManualRow = (id, field, value) => {
    this.setState((prev) => ({
      manualNextWeekRows: prev.manualNextWeekRows.map((r) =>
        r.id === id
          ? {
              ...r,
              [field]:
                field === 'manager' ? value : value === '' ? '' : Number(value),
            }
          : r
      ),
    }));
  };

  toggleEditRow = (id) => {
    this.setState((prev) => ({
      manualNextWeekRows: prev.manualNextWeekRows.map((r) =>
        r.id === id
          ? { ...r, isEditing: !r.isEditing, _original: r.isEditing ? undefined : { ...r } }
          : r
      ),
    }));
  };

  // FIX: cancel edit restores original values instead of deleting the row
  cancelManualRowEdit = (id) => {
    this.setState((prev) => {
      const row = prev.manualNextWeekRows.find((r) => r.id === id);
      // If the row was just added (no _original), remove it entirely
      if (!row || !row._original) {
        return {
          manualNextWeekRows: prev.manualNextWeekRows.filter((r) => r.id !== id),
        };
      }
      // Otherwise restore the original values and exit edit mode
      return {
        manualNextWeekRows: prev.manualNextWeekRows.map((r) =>
          r.id === id
            ? { ...r._original, isEditing: false, _original: undefined }
            : r
        ),
      };
    });
  };

  getManualTotal = () => {
    const allRows = [
      ...(this.state.autoRows || []),
      ...this.state.manualNextWeekRows,
    ];
    const eric = allRows.reduce((sum, r) => sum + (r.eric || 0), 0);
    const profit = allRows.reduce((sum, r) => sum + (r.profit || 0), 0);
    const atmDidntUse = allRows.reduce(
      (sum, r) => sum + (r.atmDidntUse || 0),
      0
    );
    const advEly = allRows.reduce((sum, r) => sum + (r.advEly || 0), 0);

    return {
      eric,
      profit,
      atmDidntUse,
      advEly,
      total: eric + profit + atmDidntUse + advEly,
    };
  };

  removeManualRow = (id) => {
    this.setState({
      manualNextWeekRows: this.state.manualNextWeekRows.filter(
        (r) => r.id !== id
      ),
    });
  };

  // Confirm helpers: close the row then immediately persist
  confirmLastWeekRow = (manager) => {
    this.setState(
      (prev) => ({
        lastWeekRows: prev.lastWeekRows.map((r) =>
          r.manager === manager
            ? { ...r, isEditing: false, _original: undefined }
            : r
        ),
      }),
      () => this.saveWeek()
    );
  };

  confirmAutoRow = (id) => {
    this.setState(
      (prev) => ({
        autoRows: prev.autoRows.map((r) =>
          r.id === id
            ? { ...r, isEditing: false, _original: undefined }
            : r
        ),
      }),
      () => this.saveWeek()
    );
  };

  confirmManualRow = (id) => {
    this.setState(
      (prev) => ({
        manualNextWeekRows: prev.manualNextWeekRows.map((r) =>
          r.id === id
            ? { ...r, isEditing: false, _original: undefined }
            : r
        ),
      }),
      () => this.saveWeek()
    );
  };

  saveWeek = async () => {
    const { selectedYear, selectedMonth, selectedWeek } = this.state;

    // FIX: close ALL open edit rows before saving
    this.setState(
      (prev) => ({
        saving: true,
        lastWeekRows: prev.lastWeekRows.map((r) => ({
          ...r,
          isEditing: false,
          _original: undefined,
        })),
        autoRows: prev.autoRows.map((r) => ({
          ...r,
          isEditing: false,
          _original: undefined,
        })),
        manualNextWeekRows: prev.manualNextWeekRows.map((r) => ({
          ...r,
          isEditing: false,
          _original: undefined,
        })),
      }),
      () => {
        const payload = {
          year: selectedYear,
          month: selectedMonth + 1,
          weekNumber: selectedWeek.weekNumber,
          fromDate: selectedWeek.fromDate,
          toDate: selectedWeek.toDate,

          atmSummary: this.state.atmSummary,
          lastWeekRows: this.state.lastWeekRows.map((r) => ({
            manager: r.manager,
            atmLastWeek: r.atmLastWeek,
            midWeekAdv: r.midWeekAdv,
          })),
          autoRows: this.state.autoRows.map((r) => ({
            id: r.id,
            manager: r.manager,
            eric: r.eric,
            profit: r.profit,
            advEly: r.advEly,
            atmDidntUse: r.atmDidntUse,
          })),
          manualNextWeekRows: this.state.manualNextWeekRows.map((r) => ({
            id: r.id,
            manager: r.manager,
            eric: r.eric,
            profit: r.profit,
            advEly: r.advEly,
            atmDidntUse: r.atmDidntUse,
          })),
        };

        this.props.apiRequest({
          key: 'atm/saveWeek',
          endpoint: LIZ_ACCOUNTING.SAVE_ATM_WEEK_DATA,
          data: payload,
          silent: true,
          onSuccess: () => {
            this.setState({
              originalManualRows: [...this.state.manualNextWeekRows],
              saving: false,
            });
            this.openFeedback('success', 'Week saved successfully');
          },
          onError: () => {
            this.openFeedback('error', 'Save failed');
            this.setState({ saving: false });
          },
        });
      }
    );
  };

  fetchLocations = () => {
    this.props.apiRequest({
      key: 'atm/locations',
      method: 'GET',
      endpoint: LOCATION.LIST,
      silent: true,
      onSuccess: (payload) => {
        if (payload?.success) {
          this.setState({ locations: payload.locations });
        } else {
          this.openFeedback('error', 'Failed to fetch locations');
        }
      },
      onError: (err) =>
        this.openFeedback('error', `Failed to fetch locations: ${err}`),
    });
  };

  generateWeeksForMonth = (year, monthIndex) => {
    const weeks = [];
    let date = startOfMonth(new Date(year, monthIndex));

    while (date.getDay() !== 2) {
      date = addDays(date, 1);
    }

    let week = 1;
    while (date <= endOfMonth(new Date(year, monthIndex))) {
      const fromDate = new Date(date);
      const toDate = addDays(fromDate, 6);

      weeks.push({
        weekNumber: week,
        fromDate,
        toDate,
        label: `Week ${week} (${format(
          addDays(fromDate, -1),
          'MMM d'
        )}–${format(addDays(toDate, -1), 'd')})`,
      });

      week++;
      date = addDays(date, 7);
    }

    return weeks;
  };

  openYear = (year) => {
    this.setState({
      selectedYear: year,
      selectedMonth: null,
      selectedWeek: null,
    });
  };

  openMonth = (monthIndex) => {
    this.setState({
      selectedMonth: monthIndex,
      selectedWeek: null,
    });
  };

  getPreviousWeek = (year, monthIndex, weekNumber) => {
    if (weekNumber > 1) {
      const weeks = this.generateWeeksForMonth(year, monthIndex);
      const prev = weeks.find((w) => w.weekNumber === weekNumber - 1);
      if (prev) return { year, monthIndex, week: prev };
    }

    let prevMonthIndex = monthIndex - 1;
    let prevYear = year;
    if (prevMonthIndex < 0) {
      prevMonthIndex = 11;
      prevYear = year - 1;
    }

    const prevMonthWeeks = this.generateWeeksForMonth(prevYear, prevMonthIndex);
    if (prevMonthWeeks.length === 0) return null;

    const lastWeek = prevMonthWeeks[prevMonthWeeks.length - 1];
    return { year: prevYear, monthIndex: prevMonthIndex, week: lastWeek };
  };

  openWeek = (week) => {
    const { selectedYear, selectedMonth, locations } = this.state;

    this.setState({
      selectedWeek: week,
      fromDate: week.fromDate,
      toDate: week.toDate,
      atmSummary: [],
      autoRows: [],
      lastWeekRows: [],
      manualNextWeekRows: [],
      originalManualRows: [],
    });

    this.props.apiRequest({
      key: 'atm/getWeekData',
      endpoint: LIZ_ACCOUNTING.GET_ATM_WEEK_DATA,
      data: {
        year: selectedYear,
        month: selectedMonth + 1,
        weekNumber: week.weekNumber,
      },
      silent: true,
      onError: (err) =>
        this.openFeedback('error', `Failed to load week data: ${err}`),
      onSuccess: async (res) => {
        const prevTotalsMap = await this.fetchPrevWeekTotals();
        this.setState({ _prevTotalsMap: prevTotalsMap });

        const buildLastWeekFromPrev = (savedMidWeekRows) => {
          return Object.entries(prevTotalsMap)
            .filter(
              ([manager]) =>
                manager && manager !== 'undefined' && manager !== 'null',
            )
            .map(([manager, atmLastWeek]) => {
              const savedRow = savedMidWeekRows.find(
                (r) =>
                  r.manager?.trim().toLowerCase() ===
                  manager.trim().toLowerCase(),
              );
              const midWeekAdv = savedRow ? savedRow.midWeekAdv || 0 : 0;
              return {
                manager,
                atmLastWeek,
                midWeekAdv,
                total: atmLastWeek + midWeekAdv,
                isEditing: false,
              };
            });
        };

        if (res?.success && res.week) {
          const weekData = res.week;
          const savedLastWeekRows = weekData.lastWeekRows || [];
          const lastWeekRows = buildLastWeekFromPrev(savedLastWeekRows);

          this.setState({
            atmSummary: weekData.atmSummary || [],
            autoRows: (weekData.autoRows || []).map((r) => ({
              ...r,
              id: r.id || uuidv4(),
              isEditing: false,
            })),
            manualNextWeekRows: (weekData.manualNextWeekRows || []).map((r) => ({
              ...r,
              id: r.id || uuidv4(),
              isEditing: false,
            })),
            _prevTotalsMap: prevTotalsMap,
            lastWeekRows,
          });
          return;
        }

        const freshLastWeekRows = buildLastWeekFromPrev([]);
        if (freshLastWeekRows.length > 0) {
          this.setState({ lastWeekRows: freshLastWeekRows });
        }

        if (locations.length) {
          this.fetchManagerSummary();
        }
      },
    });
  };

  fetchManagerSummary = () => {
    const { fromDate, toDate, locations } = this.state;
    if (!locations.length) return;

    const from = format(fromDate, 'yyyy-MM-dd');
    const to = format(toDate, 'yyyy-MM-dd');

    this.props.apiRequest({
      key: 'atm/managerSummary',
      endpoint: LIZ_ACCOUNTING.LOCATION_SUMMARY,
      data: {
        fromDate: from,
        toDate: to,
        locations: locations.map((l) => l.name),
      },
      silent: true,
      onError: (err) =>
        this.openFeedback('error', `Failed to fetch ATM summary: ${err}`),
      onSuccess: (res) => {
        if (res?.success) {
          const atmSummaries = res.sessions
            .map((session) => {
              const loc = locations.find(
                (l) =>
                  l.name.toUpperCase() === session.locationName.toUpperCase(),
              );
              if (!loc) return null;
              return {
                location: loc.name,
                summary: session.managerSummary,
              };
            })
            .filter(Boolean);

          this.setState({ atmSummary: atmSummaries }, () => {
            this.initAutoRows();
            this.initLastWeekRows(this.state._prevTotalsMap || {});
          });
        } else {
          this.openFeedback('error', 'Failed to fetch ATM summary');
        }
      },
    });
  };

  openFeedback = (severity, message) => {
    this.setState({
      feedbackBarOpen: true,
      feedbackStatus: severity,
      feedbackMessage: message,
    });
  };

  closeFeedbackBar = () => {
    this.setState({ feedbackBarOpen: false });
  };

  render() {
    let headerButtons = [];

    if (
      this.state.selectedWeek ||
      this.state.selectedMonth ||
      this.state.selectedYear
    ) {
      headerButtons.push({
        icon: <KeyboardBackspaceIcon />,
        title: 'Back',
        handler: () => {
          if (this.state.selectedWeek) {
            this.setState({ selectedWeek: null });
          } else if (this.state.selectedMonth !== null) {
            this.setState({ selectedMonth: null });
          } else if (this.state.selectedYear !== null) {
            this.setState({ selectedYear: null });
          }
        },
      });

      if (this.state.selectedWeek) {
        headerButtons.push({
          icon: <RefreshIcon />,
          title: 'Refresh',
          handler: this.openRefreshConfirm,
        });
      }
    }

    const didNotUseRows = this.getDidNotUseRows();

    const totalDidNotUse = this.sumBy(didNotUseRows, (r) => r.amount);

    const lastWeekRows = this.getLastWeekRows();

    const lastWeekTotal = this.sumBy(lastWeekRows, (r) => r.total);

    const totalUsed = lastWeekTotal - totalDidNotUse;

    const atmRows = this.getATMOverviewRows();

    const atmTotals = atmRows.reduce(
      (acc, r) => {
        acc.fromEric += r.fromEric;
        acc.fromProfit += r.fromProfit;
        acc.total += r.total;
        return acc;
      },
      { fromEric: 0, fromProfit: 0, total: 0 }
    );

    const lastWeekGrandTotal = lastWeekRows.reduce(
      (sum, r) => sum + r.total,
      0
    );

    const lastWeekMidWeekAdvTotal = lastWeekRows.reduce(
      (sum, r) => sum + r.midWeekAdv,
      0
    );

    const lastWeekAtmTotal = lastWeekRows.reduce(
      (sum, r) => sum + r.atmLastWeek,
      0
    );

    // FIX: shared inline style for action button cells so icons are always horizontal
    const actionCellStyle = {
      width: '64px',
      padding: '0 4px',
      textAlign: 'center',
      whiteSpace: 'nowrap',
    };

    const iconBtnStyle = {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: '2px',
      display: 'inline-flex',
      alignItems: 'center',
      verticalAlign: 'middle',
    };

    return (
      <div>
        <CustomToolbar
          title={
            this.state.selectedWeek
              ? `Week of ${format(
                  subDays(this.state.selectedWeek.fromDate, 1),
                  'MMM d, yyyy'
                )}`
              : this.state.selectedMonth !== null
              ? `${MONTHS[this.state.selectedMonth]} ${this.state.selectedYear}`
              : this.state.selectedYear !== null
              ? `Year ${this.state.selectedYear}`
              : 'ATM'
          }
          headerButtons={headerButtons}
        />

        <FeedbackBar
          open={this.state.feedbackBarOpen}
          severity={this.state.feedbackStatus}
          message={this.state.feedbackMessage}
          handleClose={this.closeFeedbackBar}
        />

        <div className="root-atm-receipts">
          <Dialog
            open={this.state.confirmRefreshOpen}
            onClose={() => this.setState({ confirmRefreshOpen: false })}
            PaperProps={{
              sx: { borderRadius: 3, padding: 2, minWidth: 400 },
            }}
          >
            <DialogTitle sx={{ fontWeight: 'bold', fontSize: '1.25rem' }}>
              Refresh ATM Records
            </DialogTitle>

            <DialogContent dividers>
              <DialogContentText
                sx={{ mb: 2, fontSize: '0.95rem', color: 'text.secondary' }}
              >
                This action will refresh all records from the Manager Summary.
              </DialogContentText>
              <DialogContentText sx={{ fontWeight: 'medium', mb: 1 }}>
                Only manual entries will <b>remain unchanged</b>.
              </DialogContentText>
              <DialogContentText sx={{ color: 'text.primary' }}>
                Are you sure you want to continue?
              </DialogContentText>
            </DialogContent>

            <DialogActions sx={{ justifyContent: 'center', gap: 2, mb: 1 }}>
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => this.setState({ confirmRefreshOpen: false })}
                sx={{ minWidth: 120 }}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                color="primary"
                onClick={this.refreshFromManagerSummary}
                disabled={this.state.refreshing}
                sx={{ minWidth: 120 }}
              >
                {this.state.refreshing ? 'Refreshing...' : 'Refresh'}
              </Button>
            </DialogActions>
          </Dialog>

          {!this.state.selectedYear && (
            <Grid container spacing={4}>
              {this.state.YEARS.map((year) => (
                <Grid item xs={12} sm={6} md={3} key={year}>
                  <FolderCard
                    title={year}
                    subtitle="Accounting Year"
                    variant="year"
                    onClick={() => this.openYear(year)}
                  />
                </Grid>
              ))}
            </Grid>
          )}

          {this.state.selectedYear !== null &&
            this.state.selectedMonth === null && (
              <Grid container spacing={3}>
                {MONTHS.map((month, idx) => (
                  <Grid item xs={12} sm={6} md={3} key={month}>
                    <FolderCard
                      title={month}
                      subtitle="Accounting Month"
                      variant="month"
                      onClick={() => this.openMonth(idx)}
                    />
                  </Grid>
                ))}
              </Grid>
            )}

          {this.state.selectedYear !== null &&
            this.state.selectedMonth !== null &&
            !this.state.selectedWeek && (
              <Grid container spacing={3}>
                {this.generateWeeksForMonth(
                  this.state.selectedYear,
                  this.state.selectedMonth
                ).map((week) => (
                  <Grid item xs={12} sm={6} md={4} key={week.weekNumber}>
                    <FolderCard
                      title={week.label}
                      subtitle="Accounting Week"
                      variant="week"
                      onClick={() => this.openWeek(week)}
                    />
                  </Grid>
                ))}
              </Grid>
            )}

          {this.state.selectedWeek && (
            <div className="atm-wrapper">
              <div className="atm-filters">
                <FormGroup row>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={this.state.showATM}
                        onChange={() => this.toggleSection('showATM')}
                        color="primary"
                      />
                    }
                    label="ATM Summary"
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={this.state.showLastWeek}
                        onChange={() => this.toggleSection('showLastWeek')}
                        color="primary"
                      />
                    }
                    label="ATM Last Week"
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={this.state.showNextWeek}
                        onChange={() => this.toggleSection('showNextWeek')}
                        color="primary"
                      />
                    }
                    label="ATM Next Week"
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={this.state.showDidNotUse}
                        onChange={() => this.toggleSection('showDidNotUse')}
                        color="primary"
                      />
                    }
                    label="ATM Didn't Use"
                  />
                </FormGroup>
              </div>

              {/* TABLE 1 — ATM Summary */}
              {this.state.showATM && (
                <div className="atm-card">
                  <h3>ATM Summary</h3>
                  <div style={{ overflowX: 'auto' }}>
                    <table className="atm-table">
                      <thead>
                        <tr>
                          <th className="header">ATM</th>
                          <th className="amount-header">ATM Cash</th>
                          <th className="amount-header">GM Profit</th>
                          <th className="amount-header">Total</th>
                        </tr>
                      </thead>

                      <tbody>
                        {atmRows.map((r) => (
                          <tr key={r.location}>
                            <td>{r.location}</td>
                            <td>${r.fromEric.toLocaleString()}</td>
                            <td>${r.fromProfit.toLocaleString()}</td>
                            <td className="amount-cell">
                              ${r.total.toLocaleString()}
                            </td>
                          </tr>
                        ))}

                        <tr className="summary-row">
                          <td className="bold">TOTAL</td>
                          <td className="amount-cell bold">
                            ${atmTotals.fromEric.toLocaleString()}
                          </td>
                          <td className="amount-cell bold">
                            ${atmTotals.fromProfit.toLocaleString()}
                          </td>
                          <td className="amount-cell bold">
                            ${atmTotals.total.toLocaleString()}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* TABLE 2 — ATM Last Week */}
              {this.state.showLastWeek && (
                <div className="atm-card">
                  <h3>ATM Last Week</h3>
                  <div style={{ overflowX: 'auto' }}>
                    <table className="atm-table">
                      <thead>
                        <tr>
                          {/* FIX: explicit min-width so the 2-icon cell never wraps */}
                          <th style={{ width: '64px', minWidth: '64px', padding: '0 4px' }} />
                          <th
                            className="header"
                            style={{ textAlign: 'left', paddingLeft: '8px' }}
                          >
                            Manager
                          </th>
                          <th className="amount-header">ATM Last Week</th>
                          <th className="amount-header">Mid Week ADV</th>
                          <th className="amount-header">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lastWeekRows.map((r) => (
                          <tr key={`last-${r.manager}`}>
                            {/* FIX: td uses flex row to keep icons side-by-side */}
                            <td style={actionCellStyle}>
                              {r.isEditing ? (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
                                  <button
                                    style={iconBtnStyle}
                                    onClick={() => this.cancelLastWeekRowEdit(r.manager)}
                                    title="Cancel"
                                  >
                                    <RemoveCircleIcon color="error" fontSize="small" />
                                  </button>
                                  <button
                                    style={iconBtnStyle}
                                    onClick={() => this.confirmLastWeekRow(r.manager)}
                                    title="Confirm"
                                  >
                                    <CheckCircleIcon color="success" fontSize="small" />
                                  </button>
                                </span>
                              ) : (
                                <button
                                  style={iconBtnStyle}
                                  onClick={() => this.toggleLastWeekRowEdit(r.manager)}
                                  title="Edit"
                                >
                                  <EditIcon color="primary" fontSize="small" />
                                </button>
                              )}
                            </td>
                            <td
                              style={{ textAlign: 'left', paddingLeft: '8px' }}
                            >
                              {r.manager}
                            </td>
                            <td>${r.atmLastWeek.toLocaleString()}</td>
                            <td>
                              {r.isEditing ? (
                                <TextField
                                  variant="outlined"
                                  size="small"
                                  type="number"
                                  value={r.midWeekAdv}
                                  onChange={(e) =>
                                    this.updateLastWeekRow(
                                      r.manager,
                                      e.target.value
                                    )
                                  }
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        $
                                      </InputAdornment>
                                    ),
                                    inputProps: {
                                      style: {
                                        textAlign: 'right',
                                        width: '80px',
                                      },
                                    },
                                  }}
                                />
                              ) : (
                                `$${r.midWeekAdv.toLocaleString()}`
                              )}
                            </td>
                            <td>${r.total.toLocaleString()}</td>
                          </tr>
                        ))}

                        <tr className="summary-row">
                          <td style={{ width: '64px', padding: '0 4px' }} />
                          <td
                            className="bold"
                            style={{ textAlign: 'left', paddingLeft: '8px' }}
                          >
                            TOTAL
                          </td>
                          <td className="amount-cell bold">
                            ${lastWeekAtmTotal.toLocaleString()}
                          </td>
                          <td className="amount-cell bold">
                            ${lastWeekMidWeekAdvTotal.toLocaleString()}
                          </td>
                          <td className="amount-cell bold">
                            ${lastWeekGrandTotal.toLocaleString()}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* TABLE 3 — ATM Didn't Use */}
              {this.state.showDidNotUse && (
                <div className="atm-card warning">
                  <h3>ATM Didn't Use</h3>
                  <div style={{ overflowX: 'auto' }}>
                    <table className="atm-table">
                      <tbody>
                        {didNotUseRows.map((r) => (
                          <tr key={r.id}>
                            <td>{r.manager}</td>
                            <td>${r.amount.toLocaleString()}</td>
                          </tr>
                        ))}

                        <tr className="summary-row">
                          <td className="bold">Total Didn't Use</td>
                          <td className="amount-cell bold">
                            ${totalDidNotUse.toLocaleString()}
                          </td>
                        </tr>

                        <tr className="summary-row">
                          <td className="bold">Last Week</td>
                          <td className="amount-cell bold">
                            ${lastWeekTotal.toLocaleString()}
                          </td>
                        </tr>

                        <tr className="summary-row">
                          <td className="bold">Total Use</td>
                          <td className="amount-cell bold">
                            ${totalUsed.toLocaleString()}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* TABLE 4 — ATM Next Week */}
              {this.state.showNextWeek && (
                <div className="atm-card highlight">
                  <h3>ATM Next Week</h3>
                  <div style={{ overflowX: 'auto' }}>
                    <table className="atm-table">
                      <thead>
                        <tr>
                          <th style={{ width: '64px', minWidth: '64px', padding: '0 4px' }} />
                          <th className="header">Manager</th>
                          <th className="amount-header">ATM Cash</th>
                          <th className="amount-header">ATM ADV</th>
                          <th className="amount-header">ATM Didn't Use</th>
                          <th className="amount-header">Total</th>
                        </tr>
                      </thead>

                      <tbody>
                        {/* Auto rows */}
                        {this.state.autoRows?.map((r) => (
                          <tr key={`auto-${r.id}`} className="readonly">
                            <td style={actionCellStyle}>
                              {r.isEditing ? (
                                // FIX: wrap in inline-flex span so icons are horizontal
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
                                  <button
                                    style={iconBtnStyle}
                                    onClick={() => this.cancelAutoRowEdit(r.id)}
                                    title="Cancel"
                                  >
                                    <CloseIcon color="error" fontSize="small" />
                                  </button>
                                  <button
                                    style={iconBtnStyle}
                                    onClick={() => this.confirmAutoRow(r.id)}
                                    title="Confirm"
                                  >
                                    <CheckCircleIcon color="success" fontSize="small" />
                                  </button>
                                </span>
                              ) : (
                                <button
                                  style={iconBtnStyle}
                                  onClick={() => this.toggleAutoRowEdit(r.id)}
                                  title="Edit"
                                >
                                  <EditIcon color="primary" fontSize="small" />
                                </button>
                              )}
                            </td>

                            <td className="header">
                              {r.isEditing ? (
                                <TextField
                                  className="responsive-field"
                                  variant="outlined"
                                  size="small"
                                  type="text"
                                  value={r.manager}
                                  onChange={(e) =>
                                    this.updateAutoRow(r.id, 'manager', e.target.value)
                                  }
                                />
                              ) : (
                                r.manager
                              )}
                            </td>

                            <td>
                              {r.isEditing ? (
                                <TextField
                                  className="responsive-field"
                                  variant="outlined"
                                  size="small"
                                  type="number"
                                  value={r.eric}
                                  onChange={(e) =>
                                    this.updateAutoRow(r.id, 'eric', e.target.value)
                                  }
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">$</InputAdornment>
                                    ),
                                    inputProps: { style: { textAlign: 'right' } },
                                  }}
                                />
                              ) : (
                                `$${r.eric.toLocaleString()}`
                              )}
                            </td>

                            {/* FIX: ATM ADV (profit) was not editable — now has edit input */}
                            <td>
                              {r.isEditing ? (
                                <TextField
                                  className="responsive-field"
                                  variant="outlined"
                                  size="small"
                                  type="number"
                                  value={r.profit}
                                  onChange={(e) =>
                                    this.updateAutoRow(r.id, 'profit', e.target.value)
                                  }
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">$</InputAdornment>
                                    ),
                                    inputProps: { style: { textAlign: 'right' } },
                                  }}
                                />
                              ) : (
                                `$${r.profit.toLocaleString()}`
                              )}
                            </td>

                            <td>
                              {r.isEditing ? (
                                <TextField
                                  variant="outlined"
                                  className="responsive-field"
                                  size="small"
                                  type="number"
                                  value={r.atmDidntUse}
                                  onChange={(e) =>
                                    this.updateAutoRow(r.id, 'atmDidntUse', e.target.value)
                                  }
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">$</InputAdornment>
                                    ),
                                    inputProps: { style: { textAlign: 'right' } },
                                  }}
                                />
                              ) : (
                                `$${r.atmDidntUse.toLocaleString()}`
                              )}
                            </td>

                            <td>
                              ${(r.eric + r.profit + r.atmDidntUse).toLocaleString()}
                            </td>
                          </tr>
                        ))}

                        {/* Manual rows */}
                        {this.state.manualNextWeekRows.map((r) => (
                          <tr key={r.id} className="manual">
                            <td style={actionCellStyle}>
                              {r.isEditing ? (
                                // FIX: horizontal icon pair via inline-flex span
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
                                  <button
                                    style={iconBtnStyle}
                                    // FIX: close cancels edit (restores values or removes
                                    // brand-new row), does NOT delete existing saved rows
                                    onClick={() => this.cancelManualRowEdit(r.id)}
                                    title="Cancel"
                                  >
                                    <CloseIcon color="error" fontSize="small" />
                                  </button>
                                  <button
                                    style={iconBtnStyle}
                                    // FIX: confirm just closes the row — no auto-save on every row confirm
                                    onClick={() => this.confirmManualRow(r.id)}
                                    title="Confirm"
                                  >
                                    <CheckCircleIcon color="success" fontSize="small" />
                                  </button>
                                </span>
                              ) : (
                                <button
                                  style={iconBtnStyle}
                                  onClick={() => this.toggleEditRow(r.id)}
                                  title="Edit"
                                >
                                  <EditIcon color="primary" fontSize="small" />
                                </button>
                              )}
                            </td>

                            {r.isEditing ? (
                              <>
                                <td className="header">
                                  <TextField
                                    variant="outlined"
                                    className="responsive-field"
                                    size="small"
                                    type="text"
                                    value={r.manager}
                                    onChange={(e) =>
                                      this.updateManualRow(r.id, 'manager', e.target.value)
                                    }
                                  />
                                </td>

                                <td>
                                  <TextField
                                    variant="outlined"
                                    className="responsive-field"
                                    size="small"
                                    type="number"
                                    value={r.eric}
                                    onChange={(e) =>
                                      this.updateManualRow(r.id, 'eric', e.target.value)
                                    }
                                    InputProps={{
                                      startAdornment: (
                                        <InputAdornment position="start">$</InputAdornment>
                                      ),
                                      inputProps: { style: { textAlign: 'right' } },
                                    }}
                                  />
                                </td>

                                <td>
                                  <TextField
                                    variant="outlined"
                                    size="small"
                                    className="responsive-field"
                                    type="number"
                                    value={r.profit}
                                    onChange={(e) =>
                                      this.updateManualRow(r.id, 'profit', e.target.value)
                                    }
                                    InputProps={{
                                      startAdornment: (
                                        <InputAdornment position="start">$</InputAdornment>
                                      ),
                                      inputProps: { style: { textAlign: 'right' } },
                                    }}
                                  />
                                </td>

                                <td>
                                  <TextField
                                    variant="outlined"
                                    className="responsive-field"
                                    size="small"
                                    type="number"
                                    value={r.atmDidntUse}
                                    onChange={(e) =>
                                      this.updateManualRow(r.id, 'atmDidntUse', e.target.value)
                                    }
                                    InputProps={{
                                      startAdornment: (
                                        <InputAdornment position="start">$</InputAdornment>
                                      ),
                                      inputProps: { style: { textAlign: 'right' } },
                                    }}
                                  />
                                </td>

                                <td>
                                  ${(r.eric + r.profit + r.atmDidntUse).toLocaleString()}
                                </td>
                              </>
                            ) : (
                              <>
                                <td className="header">{r.manager}</td>
                                <td>${r.eric.toLocaleString()}</td>
                                <td>${r.profit.toLocaleString()}</td>
                                <td>${r.atmDidntUse.toLocaleString()}</td>
                                <td>
                                  ${(r.eric + r.profit + r.atmDidntUse).toLocaleString()}
                                </td>
                              </>
                            )}
                          </tr>
                        ))}

                        {/* Total row */}
                        <tr className="total-row">
                          <td />
                          <td className="header">
                            <b>Total</b>
                          </td>
                          <td>
                            <b>${this.getManualTotal().eric.toLocaleString()}</b>
                          </td>
                          <td>
                            <b>${this.getManualTotal().profit.toLocaleString()}</b>
                          </td>
                          <td>
                            <b>${this.getManualTotal().atmDidntUse.toLocaleString()}</b>
                          </td>
                          <td>
                            <b>${this.getManualTotal().total.toLocaleString()}</b>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="atm-actions">
                    <button style={{ visibility: 'hidden' }} />
                    <button onClick={this.addManualRow}>+ Add Row</button>
                    <button className="primary" onClick={this.saveWeek}>
                      Save
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => ({ userInfo: userInfoSelector(state) });

export default withShipment({
  mapStateToProps,
  actionCreators: { apiRequest },
}, ATMReceipt);